/* =============================================================================
   VIEWS / ADMIN / SETTINGS — Einstellungs-Tab
   =============================================================================
   - renderSettings: gesamtes Settings-UI rendern
   - updateSetting: zentrale Schreib-Funktion mit Persistenz über
     App.storage.settings.update, optimistisches lokales Update
   - applyBrandTheme: CSS-Custom-Properties aus Brand-Farben
   - getSettingsSavedHint: Status-Text "Zuletzt gespeichert um …"

   Persistenz: Settings landen als JSONB-Spalte in restaurants.settings.
   Die Migration `seed_default_email_templates` (Session 6) hat die
   Templates idempotent ergänzt.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { spontaneousReserveSeats } from '../../core/capacity.js';
// S66 Refactor: Display-Block in eigene Datei ausgelagert (settings-displays.js).
// Re-Exports für Backward-Compatibility — alte Caller importieren weiterhin
// renderDisplaysSection + display*-Action-Handler aus settings.js.
import {
  renderDisplaysSection,
  renderDisplayCard,
  renderDisplayCardInner,
  renderDisplayPreview,
  displaySetThreshold,
  displaySetPreorderLead,
  displayToggleField,
  displayMoveField
} from './settings-displays.js';
export {
  renderDisplaysSection,
  renderDisplayCard,
  renderDisplayCardInner,
  renderDisplayPreview,
  displaySetThreshold,
  displaySetPreorderLead,
  displayToggleField,
  displayMoveField
};

// S66 Refactor: Team-Sub-Tab in eigene Datei ausgelagert
// (settings-team.js, renderTeamSection + refreshTeamCard + Invite/Role/
// Toggle-Action-Handler + Test-Hooks).
import {
  renderTeamSection,
  refreshTeamCard,
  refreshTeamAuditCard,
  updateTeamInviteField,
  inviteTeamUser,
  changeTeamUserRole,
  toggleTeamUserActive,
  TEAM_UI_ROLES,
  TEAM_ROLE_LABELS,
  TEAM_ROLE_DESCRIPTIONS,
  _isUiAssignableRole,
  _isValidTeamEmail,
  _findSelfUser,
  _callerCanEditTeam,
  _sortTeamUsers,
  _splitTeamUsers,
  teamToggleSection,
  _renderRoleSelectHtml,
  _renderLinkedBadge,
  _renderInactiveSection,
  _renderTeamCardInner,
  _teamCardInnerHtml,
  _renderTeamAuditSection,
  _buildTeamAuditRowHtml,
  _formatTeamAuditAction,
  _formatTeamAuditDetails
} from './settings-team.js';
export {
  renderTeamSection,
  refreshTeamCard,
  refreshTeamAuditCard,
  updateTeamInviteField,
  inviteTeamUser,
  changeTeamUserRole,
  toggleTeamUserActive,
  TEAM_UI_ROLES,
  TEAM_ROLE_LABELS,
  TEAM_ROLE_DESCRIPTIONS,
  _isUiAssignableRole,
  _isValidTeamEmail,
  _findSelfUser,
  _callerCanEditTeam,
  _sortTeamUsers,
  _splitTeamUsers,
  teamToggleSection,
  _renderRoleSelectHtml,
  _renderLinkedBadge,
  _renderInactiveSection,
  _renderTeamCardInner,
  _teamCardInnerHtml,
  _renderTeamAuditSection,
  _buildTeamAuditRowHtml,
  _formatTeamAuditAction,
  _formatTeamAuditDetails
};

// S66 Refactor: TSE/Compliance-Block in eigene Datei ausgelagert
// (settings-compliance-tse.js, tseSettings*, refreshTse*, tseOrphan*,
// tseSetMode, tseProvision, buildTseModeSwitchConfirmMessage).
import {
  tseSettingsSkeleton,
  tseSettingsHtml,
  refreshTseSettings,
  tseOrphanSkeleton,
  refreshTseOrphanCard,
  tseOrphanRetry,
  tseSetMode,
  tseProvision,
  buildTseModeSwitchConfirmMessage,
  _wireTseOrphanSubscription,
  _unwireTseOrphanSubscription,
  _formatTseOrphanState,
  _tseOrphanStateBadgeStyle,
  _formatTseOrphanAge,
  _buildTseOrphanTableHtml
} from './settings-compliance-tse.js';
export {
  tseSettingsSkeleton,
  tseSettingsHtml,
  refreshTseSettings,
  tseOrphanSkeleton,
  refreshTseOrphanCard,
  tseOrphanRetry,
  tseSetMode,
  tseProvision,
  _wireTseOrphanSubscription,
  _unwireTseOrphanSubscription,
  _formatTseOrphanState,
  _tseOrphanStateBadgeStyle,
  _formatTseOrphanAge,
  _buildTseOrphanTableHtml
};

// S66 Refactor: Delivery-Rules-Block in eigene Datei ausgelagert
// (settings-delivery-rules.js, Zonen-Editor + Fahrer-CRUD + Uber-Integration).
import {
  renderDeliveryRulesCard,
  renderDeliveryEmptyZonesWarning,
  loadDeliveryZonesList,
  loadDeliveryZonesDeleted,
  loadUberConfigStatus,
  updateUberConfigField,
  testUberConnection,
  restoreDeliveryZone,
  updateDeliveryToggle,
  updateDeliveryMode,
  submitNewDeliveryZone,
  deleteDeliveryZone
} from './settings-delivery-rules.js';
export {
  renderDeliveryRulesCard,
  renderDeliveryEmptyZonesWarning,
  loadDeliveryZonesList,
  loadDeliveryZonesDeleted,
  loadUberConfigStatus,
  updateUberConfigField,
  testUberConnection,
  restoreDeliveryZone,
  updateDeliveryToggle,
  updateDeliveryMode,
  submitNewDeliveryZone,
  deleteDeliveryZone
};

// S225 CUT A: Vorlagen-Tab = chip-gefilterte Übersichtsliste + Modal (kein
// Sub-Sub-Tab mehr). Der dünne Dispatcher renderTemplatesSection bleibt hier
// (Cross-Cutting), delegiert aber komplett an renderTemplatesOverview.
import { renderTemplatesOverview } from './settings-templates.js';

// S79 REF-6: Regeln-Subsections (Reservierung/Bestellung/Lieferung) ausgelagert.
// Dispatcher renderRulesSection bleibt hier (Cross-Cutting). Die nur dort
// genutzten Order-Helfer (orderDefaults/getOrderSetting/PAYMENT_METHOD_LABELS)
// sind mitgewandert.
import {
  renderRulesReservationsSubsection,
  renderRulesOrdersSubsection,
  renderRulesDeliverySubsection,
  renderGuestChannelsCard        // S266 — Karte „Gast-Kanäle" über der Sub-Tab-Leiste
} from './settings-rules.js';

// S79 REF-6: Logo-/Snapshot-Helfer (DEFAULT_LOGO_URL/getBrandLogoSrc/
// getBrandSnapshot/applyBrandLogo) sind mit dem Brand-Block nach
// settings-restaurant.js gewandert. Hier wird nur noch applyBrand (in
// updateSetting + window-Export) gebraucht.
import { applyBrand } from '../../core/branding.js';
// S79 REF-6: DSGVO/GDPR-Anonymisierung ausgelagert (settings-gdpr.js). Lokal
// gebraucht: die 4 Action-Handler (window-Block) + die 6 Pure-Build-Helfer
// (Test-Hook-Block unten). renderGdprSection wird vom Compliance-Modul
// konsumiert. Die übrigen Test-Hooks werden direkt aus dem Modul re-exportiert.
import {
  triggerGdprSearch,
  gdprUseProfileEmail,
  triggerGdprPreview,
  triggerGdprAnonymize,
  _buildGdprReasonLabel,
  _buildGdprConfirmMessage,
  _buildGdprFinalChallenge,
  _validateGdprChallenge,
  _buildGdprSuccessToast,
  _buildGdprErrorToast
} from './settings-gdpr.js';
export {
  triggerGdprSearch,
  gdprUseProfileEmail,
  triggerGdprPreview,
  triggerGdprAnonymize
};
export {
  _buildGdprPreviewCardHtml,
  _buildGdprPreviewEmptyHtml,
  _buildGdprProfileCardHtml,
  _buildGdprProfileListHtml,
  _getLastGdprPreviewForTests,
  _setLastGdprPreviewForTests,
  _resetLastGdprPreviewForTests
} from './settings-gdpr.js';

// S79 REF-6: Compliance-Subsections (TSE/DSGVO/GoBD) ausgelagert.
// Dispatcher renderComplianceSection bleibt hier (Cross-Cutting).
import {
  renderComplianceTseSubsection,
  renderComplianceDsgvoSubsection,
  renderComplianceGobdSubsection
} from './settings-compliance.js';
// S167 — „Daten"-Sub-Sub-Tab + Test-Daten-Generator (seed-test-data.html) entfernt (Jonas).
// S83-Nachzug: Default-Vergleich beim Speichern (Wert == Default → Override leeren).
import { getMailTemplateDefault } from '../../core/mail-template-defaults.js';

// S79 REF-6: Restaurant-Section (Stammdaten/Brand/Mail) + Brand-Logo/Theme +
// Action-Handler ausgelagert. Dispatcher renderRestaurantSection bleibt hier.
// Lokal gebraucht: die 3 Render-Subsections (Dispatcher) + 6 Action-Handler
// (window-Block). Re-Export: applyBrandTheme (bootstrap.js statischer Import),
// die Action-Handler (externe Surface) + Test-Hook _buildBrandLogoStatusLabel.
import {
  renderRestaurantMainSubsection,
  renderRestaurantBrandSubsection,
  renderRestaurantMailSubsection,
  uploadBrandLogo,
  resetBrandLogo,
  updateBrandSenderField,
  updateRestaurantMeta,
  applyBrandTheme,
  updateBrandField,
  applyLogoColors
} from './settings-restaurant.js';
export {
  uploadBrandLogo,
  resetBrandLogo,
  updateBrandSenderField,
  updateRestaurantMeta,
  applyBrandTheme,
  updateBrandField,
  applyLogoColors
};
export { _buildBrandLogoStatusLabel } from './settings-restaurant.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

let _lastSettingsSavedAt = null;
// S62 Hotfix-3+5 — Sub-Sub-Tabs für Settings-Bereiche. Jonas-Direktive: lange
// Sub-Tabs (Regeln / Restaurant / Vorlagen / Compliance) in jeweils thematisch
// klare Sub-Sub-Tabs splitten, alle im gleichen Brand-Pill-Style (HF4).
// Persistiert im Module-Scope (überlebt re-render aber nicht Page-Reload —
// bewusste Wahl, immer mit dem ersten Sub-Sub-Tab einsteigen).
const _subSubsections = {
  rules:      'reservations',
  restaurant: 'main',
  // S225 CUT A: „templates" hat keine Sub-Sub-Tabs mehr (eigene Chip-Leiste).
  compliance: 'tse'
};
export function _getRulesSubsectionForTests() { return _subSubsections.rules; }
export function _setRulesSubsectionForTests(v) { _subSubsections.rules = v; }

export function getSettingsSavedHint() {
  if (!_lastSettingsSavedAt) return 'Änderungen werden automatisch gespeichert.';
  const t = _lastSettingsSavedAt;
  const hh = String(t.getHours()).padStart(2, '0');
  const mm = String(t.getMinutes()).padStart(2, '0');
  const ss = String(t.getSeconds()).padStart(2, '0');
  return `Zuletzt gespeichert um ${hh}:${mm}:${ss}`;
}

// -----------------------------------------------------------------------------
// Sub-Tab-State (Session 20, Phase 16.3 — Jonas-Request P9)
// -----------------------------------------------------------------------------
// Aufteilung der 691-Zeilen-Monolith-Settings in 5 thematische Sub-Tabs.
// Persistiert über sessionStorage, damit ein Reload nicht zurück zu Tab 1 wirft.
// EXPORT (Phase 41 / S51): Für permissions.test.js — der Sync-Check verifiziert,
// dass SETTINGS_SUB_TAB_PERMISSIONS in public/adapters/auth.js exakt mit den
// id-Werten dieser Liste übereinstimmt. Bei Erweiterung um eine Section MUSS
// auch auth.js erweitert werden — sonst ist der Sub-Tab unsichtbar (oder
// umgekehrt nicht read/write-gegated).
export const SETTINGS_SECTIONS = [
  { id: 'restaurant', label: 'Restaurant',  icon: '🏠' },
  { id: 'rules',      label: 'Regeln',      icon: '📋' },
  { id: 'templates',  label: 'Vorlagen',    icon: '✉️' },
  { id: 'displays',   label: 'Displays',    icon: '📺' },
  { id: 'hardware',   label: 'Hardware',    icon: '🖨️' },
  // Phase 40 (Session 49): Team-Sub-Tab. User-Management via UI (statt
  // Service-Role-CLI). Owner/Admin sieht alle Tenant-User mit Invite + Edit +
  // Deactivate. RPCs: list_team_users / create_team_user /
  // update_team_user_role / set_team_user_active.
  { id: 'team',       label: 'Team',        icon: '👥' },
  // Phase 19 (Session 22): Compliance als eigener Sub-Tab. TSE/KassenSichV ist
  // regulatorisch (FinanzGB), nicht physisch wie Drucker. Aufgeteilt für klare
  // mentale Modelle: Hardware = Geräte, Compliance = gesetzliche Vorgaben.
  { id: 'compliance', label: 'Compliance',  icon: '⚖️' }
];
// Hinweis: S60 hatte zwischenzeitlich einen eigenen „Lieferung"-Sub-Tab.
// Nach Konsistenz-Audit verschoben: Liefer-Regeln + Zonen-Editor leben jetzt
// als dritte Card im „Regeln"-Sub-Tab — analog zu Reservierungs-Regeln und
// Bestell-Regeln. Damit ist die 3-Pfad-Konsistenz in einem Sub-Tab gegeben.

function getSettingsSection() {
  try {
    const v = sessionStorage.getItem('gg_settings_section');
    if (v && SETTINGS_SECTIONS.some(s => s.id === v)) return v;
  } catch (_) { /* ignore */ }
  return 'restaurant';
}
function setSettingsSection(id) {
  if (!SETTINGS_SECTIONS.some(s => s.id === id)) return;
  // Phase 41 (S50): Rollen-Gate. Manager/Staff dürfen nur Sub-Tabs sehen,
  // für die sie zumindest Read-Permission haben. Block bei fehlender Berechtigung.
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.getVisibleSettingsSubTabs === 'function') {
    const visible = window.App.auth.getVisibleSettingsSubTabs();
    if (!visible.includes(id)) {
      if (typeof window.showToast === 'function') {
        window.showToast('Für diesen Bereich fehlt dir die Berechtigung.', 'error');
      }
      return;
    }
  }
  try { sessionStorage.setItem('gg_settings_section', id); } catch (_) {}
  renderSettings();
}

/**
 * Phase 41 (S50): Liefert die Liste der für den eingeloggten User sichtbaren
 * Settings-Sub-Tab-IDs (in Reihenfolge von SETTINGS_SECTIONS). Wenn `App.auth`
 * nicht verfügbar, fällt es auf die volle Liste zurück (Tests / Pre-Bootstrap).
 */
function _visibleSettingsSections() {
  const auth = (typeof window !== 'undefined' && window.App && window.App.auth) ? window.App.auth : null;
  if (!auth || typeof auth.getVisibleSettingsSubTabs !== 'function') {
    return SETTINGS_SECTIONS.slice();
  }
  const visible = new Set(auth.getVisibleSettingsSubTabs());
  return SETTINGS_SECTIONS.filter(sec => visible.has(sec.id));
}

function renderSettingsSubtabs(activeId) {
  const sections = _visibleSettingsSections();
  return `
    <div class="settings-subtabs">
      ${sections.map(sec => `
        <button class="${sec.id === activeId ? 'active' : ''}"
                onclick="setSettingsSection('${sec.id}')"
                aria-pressed="${sec.id === activeId}">
          <span class="settings-subtab-icon" aria-hidden="true">${sec.icon}</span>
          <span class="settings-subtab-label">${sec.label}</span>
        </button>
      `).join('')}
    </div>
  `;
}

export function renderSettings() {
  let section = getSettingsSection();
  const tabEl = document.getElementById('tab-settings');
  if (!tabEl) return;

  // S260 §3 — den Read-Only-Wächter IMMER zuerst abhängen, nicht nur wenn der
  // neue Render wieder read-only ist. `_disableSettingsInputs` hat ihn bisher
  // als einziges getrennt — und die wird beim Wechsel auf „darf editieren"
  // gerade NICHT gerufen. Der Observer aus dem vorigen Render blieb also
  // hängen und hat jeden asynchron nachgeladenen Knopf erneut deaktiviert:
  // nach dem Wechsel Manager → Inhaber standen 13 Knöpfe (9 „Signatur
  // nachholen", 4 „Erneut probieren") weiter tot da, bis jemand den Tab neu
  // öffnete. Rechte, die zu STRENG greifen, sind kein Sicherheitsproblem —
  // aber man klickt gegen eine Wand, die nicht mehr da ist.
  if (_roObserver) { _roObserver.disconnect(); _roObserver = null; }
  // Nachzügler-Verdrahtung: beim Modul-Load gibt es den Event-Bus evtl. noch nicht.
  _wireSettingsOperatorRefresh();

  // Phase 41 (S50): Wenn der aktive Sub-Tab für den User unsichtbar ist
  // (z.B. nach Rollen-Wechsel oder Stale-Cache), fall zurück auf den ersten
  // sichtbaren Sub-Tab. Wenn gar keiner sichtbar — leerer State.
  const visibleSections = _visibleSettingsSections();
  if (!visibleSections.some(s => s.id === section)) {
    section = visibleSections.length > 0 ? visibleSections[0].id : null;
    if (section) {
      try { sessionStorage.setItem('gg_settings_section', section); } catch (_) {}
    }
  }

  // Phase 41 (S50): Read-Only-Modus für Manager — er sieht nur erlaubte
  // Sub-Tabs (s.o.), darf darin aber nicht editieren. CSS-Klasse +
  // Banner steuern die Anzeige; updateSetting() blockt zusätzlich.
  // S131 Phase D: zusätzlich Read-Only für die OPERATOR-Rolle Manager (System
  // sichtbar, aber nicht editierbar) — unabhängig vom Geräte-Konto.
  const isReadOnly = !!(section && (
    (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.isSettingsSubTabReadOnly === 'function'
      && window.App.auth.isSettingsSubTabReadOnly(section))
    || _operatorSystemReadOnly()
  ));

  let body;
  if (!section) {
    body = '<div class="card"><p class="hint">Du hast keine Settings-Berechtigung.</p></div>';
  } else {
    switch (section) {
      case 'restaurant': body = renderRestaurantSection(); break;
      case 'rules':      body = renderRulesSection(); break;
      case 'templates':  body = renderTemplatesSection(); break;
      case 'displays':   body = renderDisplaysSection(); break;
      case 'hardware':   body = renderHardwareSection(); break;
      case 'team':       body = renderTeamSection(); break;
      case 'compliance': body = renderComplianceSection(); break;
      default:           body = renderRestaurantSection();
    }
  }

  // Phase 41 (S51): Banner-Optik in styles.css (.settings-readonly-banner).
  // Inline-Style raus → einheitliche Optik mit den anderen Settings-Cards.
  const readOnlyBanner = isReadOnly ? `
    <div class="settings-readonly-banner" role="status" aria-live="polite">
      <span class="settings-readonly-icon" aria-hidden="true">🔒</span>
      <span><strong>Nur-Lese-Modus.</strong> Als Manager kannst du diese Einstellungen einsehen, aber nicht ändern. Schreibrechte hat nur der Inhaber.</span>
    </div>
  ` : '';

  tabEl.innerHTML = `
    <div class="settings-saved-indicator" id="settings-saved">${section ? getSettingsSavedHint() : ''}</div>
    ${section ? renderSettingsSubtabs(section) : ''}
    <div class="settings-body ${isReadOnly ? 'settings-readonly' : ''}">
      ${readOnlyBanner}${body}
    </div>
  `;

  // Phase 41 (S50): Im Read-Only-Modus alle interaktiven Elemente disablen.
  // Defense-in-depth zusätzlich zur updateSetting()-Sperre.
  if (isReadOnly) {
    _disableSettingsInputs(tabEl);
  }

  // TSE-Card asynchron mit Live-Daten füllen — nur wenn aktiver Sub-Tab Compliance ist
  if (section === 'compliance') {
    refreshTseSettings();
    // S82-5: Orphan-Reconciliation (owner/admin-RPC) nur für Schreibberechtigte
    // laden — im Read-Only-Manager-Blick ist die Karte gar nicht gerendert.
    if (!isReadOnly) {
      refreshTseOrphanCard();
      _wireTseOrphanSubscription();
    } else {
      _unwireTseOrphanSubscription();
    }
  } else {
    _unwireTseOrphanSubscription();
  }
  // Team-Liste asynchron laden — nur wenn aktiver Sub-Tab Team ist (S49 Phase 40)
  if (section === 'team') refreshTeamCard();
  // S60 — Liefer-Zonen asynchron nachladen, wenn Regeln-Sub-Tab aktiv ist.
  // Inline-Script-Workaround: innerHTML-Injection führt <script>-Tags nicht aus,
  // deshalb expliziter Call hier nach jedem Render.
  // S62 Hotfix-3 — Loader nur noch triggern, wenn der Liefer-Sub-Sub-Tab
  // aktiv ist (sonst überflüssige Network-Calls beim Wechsel auf den Tab
  // "Reservierungen" oder "Bestellungen").
  if (section === 'rules' && _subSubsections.rules === 'delivery') {
    setTimeout(() => loadDeliveryZonesList(), 0);
    // S63 Block U.6 — Uber-Config-Status async laden
    setTimeout(() => loadUberConfigStatus(), 0);
  }
}

/**
 * Phase 41 (S50): Disabled alle Inputs/Selects/Textareas/Buttons im Settings-
 * Bereich, wenn der aktuelle Sub-Tab read-only ist. Lässt Sub-Tab-Buttons
 * (Top-Tab-Navigation) und den Hinweis-Banner aktiv — Navigation darf bleiben.
 */
let _roObserver = null;

// S82-5: Ein Disable-Pass über alle interaktiven Elemente im Settings-Body.
// `.settings-ro-exempt`-Container sind ausgenommen (legitime Manager-AKTIONEN
// mit eigener Permission, z.B. DSGVO-Anonymisierung im read-only Compliance-Tab).
/** S263 (B1-Test): der ECHTE Read-Only-Pass für Vitest — damit der Exempt-Wrapper
 *  des Dienstplan-Toggles gegen die Laufzeit-Regel geprüft wird, nicht gegen
 *  eine Nachbildung. */
export function _applySettingsDisablePassForTests(body) { _applySettingsDisablePass(body); }

function _applySettingsDisablePass(body) {
  if (!body) return;
  body.querySelectorAll('input, select, textarea').forEach(el => {
    if (el.closest('.settings-ro-exempt')) return;
    el.disabled = true;
    el.setAttribute('aria-readonly', 'true');
  });
  // Buttons im body-Bereich auch — aber NICHT die Sub-Tab-Bar (steht außerhalb).
  body.querySelectorAll('button').forEach(el => {
    if (el.closest('.settings-ro-exempt')) return;
    // S89-Übergabe (Manager-RBAC): die Sub-Subtab-Navigation (.rules-subtabs,
    // z.B. Compliance → TSE/DSGVO/GoBD) ist reine Ansichts-Navigation, KEINE
    // Edit-Aktion. Im Read-Only-Modus nicht disablen — sonst erreicht der Manager
    // DSGVO/GoBD nicht. Inhalte bleiben über den restlichen Pass schreibgeschützt.
    if (el.closest('.rules-subtabs')) return;
    el.disabled = true;
    el.setAttribute('aria-disabled', 'true');
  });
}

function _disableSettingsInputs(tabEl) {
  if (!tabEl) return;
  const body = tabEl.querySelector('.settings-body');
  if (!body) return;
  _applySettingsDisablePass(body);
  // S82-5: Async nachgeladene Sektionen (Team-Liste via list_team_users,
  // TSE-Status, Liefer-Zonen) injizieren ihren Inhalt NACH diesem synchronen
  // Pass — sonst blieben ihre Buttons/Felder im Read-Only-Modus aktiv. Ein
  // MutationObserver re-disabled jeden Nachschub. childList+subtree (KEINE
  // attributes) → kein Endlos-Loop durch das Disablen selbst. Der alte
  // Observer wird vor jedem Render getrennt; der Body wird ohnehin neu erzeugt.
  if (_roObserver) { _roObserver.disconnect(); _roObserver = null; }
  if (typeof MutationObserver === 'function') {
    _roObserver = new MutationObserver(() => _applySettingsDisablePass(body));
    _roObserver.observe(body, { childList: true, subtree: true });
  }
  // Optik kommt jetzt aus styles.css (.settings-readonly + Cascade).
  // Phase 41 (S51): Click-Toast registrieren, damit User bei einem
  // Klick auf disabled-Inputs nicht ratlos sind. CSS hat
  // pointer-events:auto gesetzt → der Click bubbelt zum Body.
  _wireSettingsReadOnlyToast(body);
}

/**
 * Phase 41 (S51): Hängt einen Capture-Listener an den Settings-Body, der
 * Klicks auf disabled-Elemente im Read-Only-Modus abfängt und einen Toast
 * mit der konkreten Begründung zeigt. Throttled, damit ein versehentliches
 * Doppel-Klicken nicht mehrere Toasts erzeugt.
 *
 * Vor jedem Render wird der Body neu erzeugt → es bleibt KEIN Stale-Listener
 * hängen. Trotzdem verhindert das `_roClickWired`-Flag eine versehentliche
 * Doppel-Registrierung im selben Render-Frame.
 */
function _wireSettingsReadOnlyToast(body) {
  if (!body || body._roClickWired) return;
  body._roClickWired = true;
  body.addEventListener('mousedown', (e) => {
    const t = e.target;
    if (!t || typeof t.matches !== 'function') return;
    const isDisabled = t.matches(
      'input:disabled, select:disabled, textarea:disabled, button:disabled'
    );
    if (!isDisabled) return;
    // Throttle: ein Toast pro 1.5s. Doppelt klicken erzeugt sonst Stapel.
    const now = Date.now();
    if (now - (body._lastRoToastAt || 0) < 1500) return;
    body._lastRoToastAt = now;
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(
        'Nur-Lese-Modus — als Manager hast du hier kein Schreibrecht.',
        'info'
      );
    }
  }, { capture: true });
}

/**
 * Phase 41 (S51): Pure Helper für Tests. Gibt true zurück, wenn der Toast
 * laut Throttle-Logik aktuell gefeuert werden sollte. Test-Hook:
 * shouldFireReadOnlyToast({ lastAt, now, throttleMs })
 */
export function _shouldFireReadOnlyToast({ lastAt = 0, now = Date.now(), throttleMs = 1500 } = {}) {
  return (now - (lastAt || 0)) >= throttleMs;
}

// -----------------------------------------------------------------------------
// Section: Restaurant — Stammdaten, Adresse, Belegpflichtangaben, Öffnung, Design
// -----------------------------------------------------------------------------
function renderRestaurantSection() {
  const active = _subSubsections.restaurant;
  let body;
  if (active === 'brand')     body = renderRestaurantBrandSubsection();
  else if (active === 'mail') body = renderRestaurantMailSubsection();
  else                        body = renderRestaurantMainSubsection();
  return `
    ${_renderSubSubtabBar('restaurant')}
    ${body}
  `;
}

// -----------------------------------------------------------------------------
// Section: Regeln — 3 Sub-Sub-Tabs (Reservierungen / Bestellungen / Lieferung)
// S62 Hotfix-3 (Jonas-Direktive): „den Regel-Tab in 3 übersichtliche Unter-
// Tabs einteilen — pro Tab das ganze Fenster nutzen, um alles schön darzu-
// stellen". Jeder Sub-Bereich rendert direkt in der vollen Settings-Spalte
// statt in einem 280px-auto-fit-Grid mit zwei kleinen Spalten.
// -----------------------------------------------------------------------------

// S62 HF5 — generische Sub-Sub-Tab-Definition für alle Settings-Bereiche.
// Pro Section eine Liste der erlaubten Sub-Sub-Tabs. Wenn ein Section nicht
// in dieser Map enthalten ist (Displays/Hardware/Team/Daten), gibt es keine
// Sub-Sub-Tab-Bar und der renderXxxSection rendert direkt.
const SUB_SUBTABS = {
  rules: [
    { id: 'reservations', label: 'Reservierungs-Regeln', icon: '📅' },
    { id: 'orders',       label: 'Bestell-Regeln',       icon: '🍔' },
    { id: 'delivery',     label: 'Liefer-Regeln',        icon: '🛵' }
  ],
  restaurant: [
    { id: 'main',     label: 'Stammdaten',      icon: '🏠' },
    { id: 'brand',    label: 'Brand & Design',  icon: '🎨' },
    { id: 'mail',     label: 'Mail-Identität',  icon: '✉️' }
  ],
  // S225 CUT A: „templates" hat keine Sub-Sub-Tabs mehr — der Vorlagen-Tab
  // nutzt eine eigene Chip-Filter-Leiste (settings-templates.js).
  compliance: [
    { id: 'tse',   label: 'TSE / KassenSichV', icon: '🔐' },
    { id: 'dsgvo', label: 'DSGVO',             icon: '🛡️' },
    { id: 'gobd',  label: 'GoBD-Doku',         icon: '📋' }
  ]
};

// Backward-Compat-Alias für die alte HF3-API. Frontend ruft weiterhin
// `setRulesSubsection('orders')`; intern landet das in setSubSubsection.
export function setRulesSubsection(sub) { setSubSubsection('rules', sub); }

export function setSubSubsection(section, sub) {
  const list = SUB_SUBTABS[section];
  if (!list || !list.some(t => t.id === sub)) return;
  _subSubsections[section] = sub;
  renderSettings();
  // Async-Loader für Sub-Bereiche mit Lazy-Content
  if (section === 'rules' && sub === 'delivery') {
    setTimeout(() => loadDeliveryZonesList(), 0);
    // S63 Block U.6 — Uber-Config-Status async laden
    setTimeout(() => loadUberConfigStatus(), 0);
  }
}

function _renderSubSubtabBar(section) {
  const list = SUB_SUBTABS[section];
  if (!list) return '';
  const active = _subSubsections[section];
  return `
    <div class="rules-subtabs" role="tablist" aria-label="${htmlEscape(section)}-Bereiche">
      ${list.map(t => `
        <button type="button" role="tab"
                aria-selected="${t.id === active}"
                class="rules-subtab ${t.id === active ? 'active' : ''}"
                onclick="setSubSubsection('${section}','${t.id}')">
          <span class="rules-subtab-icon" aria-hidden="true">${t.icon}</span>
          <span class="rules-subtab-label">${htmlEscape(t.label)}</span>
        </button>
      `).join('')}
    </div>
  `;
}

function renderRulesSection() {
  const active = _subSubsections.rules;
  let body;
  if (active === 'orders')        body = renderRulesOrdersSubsection();
  else if (active === 'delivery') body = renderRulesDeliverySubsection();
  else                            body = renderRulesReservationsSubsection();
  // S266 — „Gast-Kanäle" ganz oben, ÜBER der Sub-Tab-Leiste: in jedem der drei
  // Sub-Tabs sichtbar, weil die fünf Schalter alle drei Pfade betreffen.
  return `
    <div class="settings-grid">
      ${renderGuestChannelsCard()}
    </div>
    ${_renderSubSubtabBar('rules')}
    ${body}
  `;
}

// -----------------------------------------------------------------------------
// Section: Vorlagen — E-Mail-Templates für Reservierungen + Bestellungen
// -----------------------------------------------------------------------------
function renderTemplatesSection() {
  // S225 CUT A — Übersichtsliste mit eigener Chip-Leiste (ersetzt die 6
  // Sub-Sub-Tabs). Die Chip-Filter-Logik + Zeilen-Rendering leben komplett in
  // settings-templates.js.
  return renderTemplatesOverview();
}

// -----------------------------------------------------------------------------
// Section: Displays — S66 in src/views/admin/settings-displays.js ausgelagert.
// Re-Exports siehe Import-Block ganz oben.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Section: Hardware — nur noch physische Geräte (Drucker etc.)
// Phase 19 (Session 22): TSE rausgezogen in eigenen Compliance-Sub-Tab,
// weil TSE/KassenSichV regulatorisch ist, nicht physisch.
// -----------------------------------------------------------------------------
function renderHardwareSection() {
  return `
    <div class="card" id="printers-settings-card">
      ${typeof window.renderPrintersCard === 'function' ? window.renderPrintersCard() : '<h2>Drucker</h2><p class="hint">Modul lädt…</p>'}
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Section: Compliance — TSE + GoBD-Doku + (zukünftig) DSGVO-Settings
// -----------------------------------------------------------------------------
function renderComplianceSection() {
  const active = _subSubsections.compliance;
  let body;
  if (active === 'dsgvo')      body = renderComplianceDsgvoSubsection();
  else if (active === 'gobd')  body = renderComplianceGobdSubsection();
  else                         body = renderComplianceTseSubsection();
  return `
    ${_renderSubSubtabBar('compliance')}
    ${body}
  `;
}

// -----------------------------------------------------------------------------
// Section: Lieferungs-Regeln-Card — S66 in src/views/admin/settings-delivery-rules.js
// ausgelagert (Zonen-Editor + Fahrer-CRUD + Uber-Integration). Re-Exports siehe
// Import-Block ganz oben.
// -----------------------------------------------------------------------------


// -----------------------------------------------------------------------------
// Section: Daten — S167 entfernt (Jonas): Sub-Sub-Tab + Test-Daten-Generator
// (settings-data.js + seed-test-data.html) gelöscht; Backup/Restore läuft
// serverseitig (Service-Role), nicht im Cockpit-Frontend.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// TSE & Compliance — S66 in src/views/admin/settings-compliance-tse.js
// ausgelagert (tseSettings*, refreshTse*, tseOrphan*, tseSetMode, tseProvision,
// buildTseModeSwitchConfirmMessage). Re-Exports siehe Import-Block ganz oben.
// -----------------------------------------------------------------------------

/** S131 Phase D — System ist für die OPERATOR-Rolle Manager nur lesbar
 *  (owner/admin: voll). Unabhängig vom Geräte-Konto. */
function _operatorSystemReadOnly() {
  try {
    const OR = window.OperatorRoles;
    const opApi = window.App && window.App.operator;
    if (!OR || !opApi || typeof opApi.getOperator !== 'function') return false;
    return OR.isSystemReadOnlyForRoles(OR.effectiveRoles(opApi.getOperator()));
  } catch (_) { return false; }
}

export async function updateSetting(key, val) {
  // S131 Phase D: Auto-Save-Guard für den Operator-Read-Only-Modus (Manager).
  // Der Server gated Settings ohnehin owner/admin (update_restaurant_settings),
  // hier kommt der freundliche Hinweis ohne Toast-Spam + früher Abbruch.
  if (_operatorSystemReadOnly()) {
    if (typeof window.showToast === 'function') {
      window.showToast('Nur-Lese-Modus: Als Manager kannst du Einstellungen einsehen, aber nicht ändern.', 'info');
    }
    return;
  }
  // Phase 41 (S50): Defense-in-Depth-Rollen-Check. Die UI sollte den Aufruf
  // bereits per Sub-Tab-Visibility + Read-Only-Modus verhindern, aber falls
  // jemand via Console oder Bot direkt updateSetting() ruft, sperrt's hier.
  // Section-Lookup: aus dem aktiven Sub-Tab. DB-RLS ist die dritte Schicht.
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.canEdit === 'function') {
    const section = getSettingsSection();
    if (!window.App.auth.canEdit('settings.' + section)) {
      console.warn('[updateSetting] BLOCKIERT (Phase 41 Permission):',
        key, '→ Rolle', window.App.auth.getRole(),
        'darf settings.' + section + ' nicht editieren');
      if (typeof window.showToast === 'function') {
        window.showToast(
          'Für diese Änderung fehlt dir die Berechtigung.',
          'error'
        );
      }
      return;
    }
  }

  const state = getState();
  state.settings[key] = val;
  _lastSettingsSavedAt = new Date();
  const ind = document.getElementById('settings-saved');
  if (ind) {
    ind.textContent = 'Speichere...';
    ind.classList.add('flash');
  }
  try {
    await window.App.storage.settings.update({ [key]: val });
    if (ind) {
      ind.textContent = getSettingsSavedHint();
      clearTimeout(ind._flashTimer);
      ind._flashTimer = setTimeout(() => ind.classList.remove('flash'), 600);
    }
    // S48 Multi-Tenant-Branding-Fix: bei brand-relevanten Settings den
    // zentralen Brand-Apply triggern, damit Header-Name + Logo + Document-
    // Title sofort den neuen Wert zeigen. Bisher musste der User die Seite
    // neu laden, um Restaurant-Namens-Änderungen im Topbar-Header zu sehen.
    // applyBrand() ist idempotent + defensiv.
    if (key === 'restaurantName' || key === 'brand') {
      try { applyBrand(); }
      catch (brandErr) { console.warn('[updateSetting] applyBrand:', brandErr); }
    }
  } catch (e) {
    console.error('[updateSetting] Persistenz fehlgeschlagen:', e);
    showToast('Einstellung konnte nicht gespeichert werden: ' + (e.message || e), 'error');
    if (ind) {
      ind.textContent = 'Fehler beim Speichern';
      ind.classList.remove('flash');
    }
  }
}

/**
 * S83-Nachzug: Gemeinsame Schreib-Logik für alle drei Mail-Vorlagen-Säulen.
 * Vorbefüll-Modell — die Felder zeigen den effektiven Text (Override sonst
 * Default). Beim Speichern:
 *   • Wert == aktueller Default → Override-Feld LEEREN (das Feld bleibt damit
 *     auto-aktualisierbar, wenn sich der Default später ändert; kein toter
 *     Duplikat-Override). Wird das Kind-Objekt leer → ganz entfernen.
 *   • sonst → Override speichern.
 * Speichert das komplette settingsKey-Sub-Objekt zurück (JSONB-Merge-Konsistenz).
 */
function _applyTemplateField(settingsKey, kind, field, value) {
  const state = getState();
  const cur = { ...((state.settings && state.settings[settingsKey]) || {}) };
  const existing = { ...(cur[kind] || {}) };
  const def = getMailTemplateDefault(settingsKey, kind);
  if (def && typeof value === 'string' && value === def[field]) {
    delete existing[field];
  } else {
    existing[field] = value;
  }
  if (Object.keys(existing).length === 0) delete cur[kind];
  else cur[kind] = existing;
  return updateSetting(settingsKey, cur);
}

export async function updateOrderTemplate(kind, field, value) {
  return _applyTemplateField('emailTemplatesOrder', kind, field, value);
}

export async function updateDeliveryTemplate(kind, field, value) {
  return _applyTemplateField('emailTemplatesDelivery', kind, field, value);
}

export async function updateReservationTemplate(kind, field, value) {
  return _applyTemplateField('emailTemplates', kind, field, value);
}

// S85: Kassenbon nutzt die bestehende emailTemplatesOrder-Säule (resolveOrderTemplate
// liest emailTemplatesOrder.receipt) → updateOrderTemplate genügt, kein eigener
// Handler. Export (Z-Bon an Steuerberater) hat eine eigene Säule:
export async function updateExportTemplate(kind, field, value) {
  return _applyTemplateField('emailTemplatesExport', kind, field, value);
}

// S226: Newsletter-Säule (Double-Opt-in-Bestätigung, emailTemplatesNewsletter).
export async function updateNewsletterTemplate(kind, field, value) {
  return _applyTemplateField('emailTemplatesNewsletter', kind, field, value);
}

/**
 * S83-Nachzug: Override eines Kinds komplett entfernen → das Feld zeigt wieder
 * den (auto-aktualisierbaren) Default. Aufgerufen vom „↩ Auf Standard
 * zurücksetzen"-Button im Vorlagen-Editor.
 */
export async function resetMailTemplate(settingsKey, kind) {
  const VALID = ['emailTemplatesOrder', 'emailTemplatesDelivery', 'emailTemplates', 'emailTemplatesExport', 'emailTemplatesNewsletter'];
  if (!VALID.includes(settingsKey)) return;
  const state = getState();
  const cur = { ...((state.settings && state.settings[settingsKey]) || {}) };
  if (!(kind in cur)) return;
  delete cur[kind];
  await updateSetting(settingsKey, cur);
  // S95 — nach dem Reset die Vorlagen-Sektion neu rendern: das Eingabefeld muss
  // sofort wieder den Default zeigen, das „angepasst"-Badge verschwinden und der
  // Reset-Button disabled werden. `updateSetting` persistiert nur, rendert nicht
  // (onchange-Felder brauchen kein Re-Render — der Reset schon). _subSubsections
  // bleibt erhalten → renderSettings rendert dieselbe Sub-Sub-Sektion neu.
  renderSettings();
}

/**
 * Bezahlmethode an-/abschalten. Settings-Array bleibt deterministisch sortiert,
 * damit der Cockpit-Dialog die Buttons immer in derselben Reihenfolge zeigt.
 */
export async function updateOrderPaymentMethod(method, enabled) {
  const ORDER = ['cash', 'card', 'stripe'];
  const state = getState();
  const cur   = Array.isArray(state.settings.paymentMethods) ? state.settings.paymentMethods : ['cash', 'card'];
  const setNext = new Set(cur);
  if (enabled) setNext.add(method); else setNext.delete(method);
  const next = ORDER.filter(m => setNext.has(m));
  await updateSetting('paymentMethods', next);
}

/**
 * S234 T3 — Stripe-Online-Bezahlung je Kanal togglen (`settings.stripeChannels`).
 * Fehlt das Feld, gelten Takeaway + Lieferung als an (kein Regress gegenüber
 * „nur Takeaway"). Reihenfolge deterministisch.
 *
 * S246 — `dine-in` kommt dazu (Bezahlen am Tisch). Bewusst NICHT im Default:
 * ein Betrieb, der bisher nur Abholung online kassiert hat, soll nicht über
 * Nacht Gäste am Tisch selbst zahlen lassen. Wer das will, schaltet es ein.
 */
export async function updateStripeChannel(channel, enabled) {
  const ORDER = ['takeaway', 'delivery', 'dine-in'];
  if (!ORDER.includes(channel)) return;
  const state = getState();
  const cur = Array.isArray(state.settings.stripeChannels)
    ? state.settings.stripeChannels : ORDER.slice();
  const setNext = new Set(cur);
  if (enabled) setNext.add(channel); else setNext.delete(channel);
  const next = ORDER.filter(c => setNext.has(c));
  await updateSetting('stripeChannels', next);
}

// -----------------------------------------------------------------------------
// Restaurant-Section (Stammdaten/Brand/Mail) + Brand-Logo/Theme + Brand-Action-
// Handler — S79 in src/views/admin/settings-restaurant.js ausgelagert.
// Dispatcher renderRestaurantSection bleibt oben (Cross-Cutting). Re-Exports
// (applyBrandTheme für bootstrap.js + Test-Hook _buildBrandLogoStatusLabel)
// siehe Import-Block ganz oben.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Team-Sub-Tab — S66 in src/views/admin/settings-team.js ausgelagert
// (renderTeamSection, refreshTeamCard, refreshTeamAuditCard, invite/role/
// toggle Action-Handler + Test-Hooks). Re-Exports siehe Import-Block oben.
// -----------------------------------------------------------------------------


// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderSettings,
    setSettingsSection,
    updateSetting,
    updateOrderTemplate,
    updateDeliveryTemplate,
    updateReservationTemplate,
    updateExportTemplate,         // S85: Z-Bon-Export-Vorlage
    updateNewsletterTemplate,     // S226: Newsletter-DOI-Vorlage
    resetMailTemplate,            // S83-Nachzug: „Auf Standard zurücksetzen"
    updateOrderPaymentMethod,
    updateStripeChannel,          // S234 T3: Stripe je Kanal (Takeaway/Lieferung)
    updateRestaurantMeta,
    uploadBrandLogo,
    resetBrandLogo,
    getSettingsSavedHint,
    // S60 / 3-Pfad — Lieferungs-Regeln
    updateDeliveryToggle,
    updateDeliveryMode,
    submitNewDeliveryZone,
    deleteDeliveryZone,
    loadDeliveryZonesList,
    loadDeliveryZonesDeleted,
    renderDeliveryEmptyZonesWarning,
    restoreDeliveryZone,
    // S63 Block U.6 — Uber-Integration
    updateUberConfigField,
    loadUberConfigStatus,
    testUberConnection,
    // S209 — Fahrer-CRUD entfernt (Fahrer = Team-User mit Rolle „Fahrer")
    // S62 Hotfix-3+5 — Regeln-/Restaurant-/Templates-/Compliance-Sub-Sub-Tab-Switch
    setRulesSubsection,
    setSubSubsection,
    applyBrand,
    applyBrandTheme,
    tseSetMode,
    tseProvision,
    // S55 Block N — TSE-Orphan-View (Compliance)
    tseOrphanRetry,
    triggerGdprAnonymize,
    triggerGdprPreview,
    triggerGdprSearch,
    gdprUseProfileEmail,
    displaySetThreshold,
    displaySetPreorderLead,
    displayToggleField,
    displayMoveField,
    // Phase 40 (S49) — Team-Sub-Tab
    refreshTeamCard,
    updateTeamInviteField,
    inviteTeamUser,
    changeTeamUserRole,
    toggleTeamUserActive,
    // S49 Block D — Mail-Sender-Override pro Tenant
    updateBrandSenderField,
    // S56 Hotfix 5 — neue brand-Felder im Restaurant-Sub-Tab
    updateBrandField,
    // S167 P4 — Brand-Farben aus Logo ableiten
    applyLogoColors
  });
}

/* -------------------------------------------------------------------------- */
/* S260 §3 — der Operator-Wechsel erreicht die Einstellungen direkt            */
/* -------------------------------------------------------------------------- */
/**
 * `shell.js` zeichnet bei `operator:changed` bereits die aktive Sicht neu. Die
 * Einstellungen hängen sich hier zusätzlich selbst dran, und zwar aus einem
 * Grund, der über den Einzelfall hinausgeht: die Read-Only-Entscheidung fällt in
 * DIESER Datei (`isReadOnly`), also gehört ihr Anlass auch hierher. Verlässt man
 * sich allein auf den Umweg über die Shell, hängt die Rechte-Anzeige an einer
 * Routing-Entscheidung — und genau da ist sie im S259-Retest hängen geblieben.
 *
 * Idempotent (ein Abo je Seitenleben) und nur, wenn der Tab wirklich sichtbar
 * ist: ein Re-Render im Hintergrund wäre verschwendet und könnte einen offenen
 * Editier-Zustand kosten.
 */
export function _wireSettingsOperatorRefresh() {
  if (typeof window === 'undefined') return;
  const ev = window.App && window.App.events;
  if (!ev || typeof ev.on !== 'function' || window._settingsOperatorSub) return;
  window._settingsOperatorSub = true;
  ev.on('operator:changed', () => {
    try {
      const tabEl = document.getElementById('tab-settings');
      if (!tabEl) return;
      const visible = tabEl.offsetParent !== null || tabEl.style.display === 'block';
      if (!visible) return;
      renderSettings();
    } catch (e) {
      console.warn('[settings] operator:changed:', e && e.message);
    }
  });
}

if (typeof window !== 'undefined') {
  // Beim Modul-Load versuchen; ist der Event-Bus noch nicht da, holt es der
  // erste `renderSettings()`-Aufruf nach (siehe unten).
  _wireSettingsOperatorRefresh();
}

// -----------------------------------------------------------------------------
// Test-Hook-Exports (S35) — additive named exports für Vitest. Zur Laufzeit
// ein No-Op (kein zusätzlicher Code-Pfad). Pattern aus datev-export.js +
// day-close.js + orders.js, Memory-Lesson feedback_gg_hard_way.
// -----------------------------------------------------------------------------
export {
  buildTseModeSwitchConfirmMessage as _buildTseModeSwitchConfirmMessage,
  _buildGdprReasonLabel,
  _buildGdprConfirmMessage,
  _buildGdprFinalChallenge,
  _validateGdprChallenge,
  _buildGdprSuccessToast,
  _buildGdprErrorToast,
  // Phase 40 (S49) — Team-Sub-Tab Test-Hooks
  TEAM_UI_ROLES as _TEAM_UI_ROLES,
  TEAM_ROLE_LABELS as _TEAM_ROLE_LABELS
};
